import { useEffect, useMemo, useState } from "react";
import {
  completeOnboarding,
  getCurrentBusinessContext,
  saveManualBusinessContext,
  saveProfileProgress,
} from "../application/round02-service";
import type { CEOOSDatabase } from "../db/database";
import { isValidTimeZone } from "../domain/business-date";
import type { CEOProfile, ConstitutionVersion, FieldMode } from "../domain/types";
import { createFullBackup } from "../import-export/backup";
import {
  commitBoardContextImport,
  preflightBoardContextImport,
} from "../import-export/board-import";
import { downloadJson, readJsonFile } from "../import-export/file";
import { safeErrorMessage } from "../privacy/policy";

interface ManualContextDraft {
  current180DayMainline: string;
  currentConstraint: string;
  todayBusinessBattle: string;
  currentMaximumRisk: string;
  noStartList: string[];
  tomorrowBusinessAction: string;
}

const EMPTY_CONTEXT: ManualContextDraft = {
  current180DayMainline: "",
  currentConstraint: "",
  todayBusinessBattle: "",
  currentMaximumRisk: "",
  noStartList: [],
  tomorrowBusinessAction: "",
};

const FIELD_OPTIONS: Array<{ id: FieldMode; label: string; question: string }> = [
  {
    id: "shenzhen",
    label: "深圳",
    question: "今天我把什么不确定性变成了可验证结果？",
  },
  {
    id: "hong_kong",
    label: "香港",
    question: "今天我增加了什么信任、准确性、合规性或可托付性？",
  },
  {
    id: "gba",
    label: "大湾区跨境",
    question: "今天我消除了哪一项跨境摩擦？",
  },
  { id: "custom", label: "其他／自定义", question: "" },
];

export function Onboarding({
  database,
  initialProfile,
  dateKey,
  onComplete,
}: {
  database: CEOOSDatabase;
  initialProfile: CEOProfile;
  dateKey: string;
  onComplete: (profile: CEOProfile) => void;
}) {
  const [profile, setProfile] = useState(initialProfile);
  const [sourceMode, setSourceMode] = useState<"manual" | "board">("manual");
  const [context, setContext] = useState<ManualContextDraft>(EMPTY_CONTEXT);
  const [constitution, setConstitution] = useState<ConstitutionVersion>();
  const [constitutionChecked, setConstitutionChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [saveState, setSaveState] = useState("准备保存");
  const [notice, setNotice] = useState("");
  const step = profile.onboardingStep;
  const hasContextContent = Boolean(
    context.current180DayMainline.trim() ||
    context.currentConstraint.trim() ||
    context.todayBusinessBattle.trim() ||
    context.currentMaximumRisk.trim() ||
    context.tomorrowBusinessAction.trim() ||
    context.noStartList.length,
  );

  useEffect(() => {
    let active = true;
    void Promise.all([
      database.constitutions.get(profile.currentConstitutionVersionId),
      getCurrentBusinessContext(database),
    ])
      .then(([nextConstitution, existingContext]) => {
        if (!active) return;
        setConstitution(nextConstitution);
        if (existingContext) {
          setSourceMode(existingContext.source === "board_import" ? "board" : "manual");
          setContext({
            current180DayMainline: existingContext.current180DayMainline,
            currentConstraint: existingContext.currentConstraint,
            todayBusinessBattle: existingContext.todayBusinessBattle,
            currentMaximumRisk: existingContext.currentMaximumRisk,
            noStartList: existingContext.noStartList,
            tomorrowBusinessAction: existingContext.tomorrowBusinessAction,
          });
        }
      })
      .catch((error) => {
        if (active) setNotice(safeErrorMessage(error));
      });
    return () => {
      active = false;
    };
  }, [database, profile.currentConstitutionVersionId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSaveState("保存中…");
      void saveProfileProgress(database, profile)
        .then(() =>
          setSaveState(
            `已自动保存 · ${new Date().toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}`,
          ),
        )
        .catch(() => setSaveState("自动保存失败，请重试"));
    }, 450);
    return () => window.clearTimeout(timer);
  }, [database, profile]);

  useEffect(() => {
    if (sourceMode !== "manual" || step < 2 || !hasContextContent) return;
    const timer = window.setTimeout(() => {
      void saveManualBusinessContext(
        database,
        profile.id,
        dateKey,
        context,
        new Date().toISOString(),
        { audit: false },
      ).catch(() => setSaveState("业务上下文自动保存失败"));
    }, 700);
    return () => window.clearTimeout(timer);
  }, [context, database, dateKey, hasContextContent, profile.id, sourceMode, step]);

  const canContinue = useMemo(() => {
    if (step === 1)
      return Boolean(isValidTimeZone(profile.businessTimezone) && profile.preferredLocale);
    if (step === 4) return constitutionChecked;
    if (step === 5) return privacyChecked;
    return true;
  }, [
    constitutionChecked,
    privacyChecked,
    profile.businessTimezone,
    profile.preferredLocale,
    step,
  ]);

  const move = async (direction: -1 | 1) => {
    try {
      if (step === 1 && direction === 1 && !isValidTimeZone(profile.businessTimezone)) {
        setNotice("业务时区无效。请选择 IANA 时区，例如 Asia/Shanghai。");
        return;
      }
      if (step === 2 && direction === 1 && sourceMode === "manual" && hasContextContent) {
        await saveManualBusinessContext(
          database,
          profile.id,
          dateKey,
          context,
          new Date().toISOString(),
        );
      }
      const nextStep = Math.min(5, Math.max(1, step + direction)) as 1 | 2 | 3 | 4 | 5;
      const next = { ...profile, onboardingStep: nextStep };
      setProfile(await saveProfileProgress(database, next));
      setNotice("");
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  const handleBoardFile = async (file: File) => {
    try {
      const value = await readJsonFile(file);
      const preflight = await preflightBoardContextImport(database, value, {
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
      });
      if (preflight.result.status === "rejected") {
        setNotice(
          `文件未导入：${preflight.result.errors.map((item) => `${item.path} ${item.message}`).join("；")}`,
        );
        return;
      }
      const acceptConflict =
        preflight.result.status === "needs_confirmation"
          ? window.confirm("该文件与已有导入重复或冲突。保留为可追溯版本，不覆盖现有内容？")
          : false;
      const result = await commitBoardContextImport(database, preflight, acceptConflict);
      if (result.status !== "committed") {
        setNotice("文件尚未提交；现有业务上下文未被覆盖。");
        return;
      }
      setSourceMode("board");
      const nextContext = await getCurrentBusinessContext(database);
      if (nextContext) {
        setContext({
          current180DayMainline: nextContext.current180DayMainline,
          currentConstraint: nextContext.currentConstraint,
          todayBusinessBattle: nextContext.todayBusinessBattle,
          currentMaximumRisk: nextContext.currentMaximumRisk,
          noStartList: nextContext.noStartList,
          tomorrowBusinessAction: nextContext.tomorrowBusinessAction,
        });
      }
      setNotice(`已导入 ${file.name}；来源保留为董事会合同。`);
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  const finish = async () => {
    try {
      const completed = await completeOnboarding(database, profile);
      onComplete(completed);
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  return (
    <main className="onboarding-shell">
      <header className="onboarding-header">
        <div className="brand">CEO OS</div>
        <div className="step-status" aria-live="polite">
          <span>{step} / 5</span>
          <span>{saveState}</span>
        </div>
        <div className="progress-rail" aria-hidden="true">
          <span style={{ width: `${step * 20}%` }} />
        </div>
      </header>

      {notice && (
        <div className="inline-notice" role="status">
          {notice}
        </div>
      )}

      {step === 1 && (
        <section className="flow-step" aria-labelledby="onboarding-positioning">
          <h1 id="onboarding-positioning">每天只确认一个真实纠偏</h1>
          <p className="lead">
            CEO OS 管创始人如何面对事实和行动；它不替董事会制定战略，也不是心理诊断或习惯打卡。
          </p>
          <div className="principle-rail">
            <span>发现影响经营的个人偏差</span>
            <span>确认明天唯一可验证动作</span>
            <span>第二天检查真实结果</span>
          </div>
          <label className="field">
            <span>业务时区</span>
            <input
              aria-label="业务时区"
              list="business-timezones"
              value={profile.businessTimezone}
              onChange={(event) => setProfile({ ...profile, businessTimezone: event.target.value })}
            />
            <small>历史复盘日期不会随浏览器时区自动改变。</small>
          </label>
          <datalist id="business-timezones">
            <option value="Asia/Shanghai" />
            <option value="Asia/Hong_Kong" />
            <option value="America/New_York" />
          </datalist>
          <label className="field">
            <span>界面语言</span>
            <select
              aria-label="界面语言"
              value={profile.preferredLocale}
              onChange={(event) => setProfile({ ...profile, preferredLocale: event.target.value })}
            >
              <option value="zh-CN">简体中文</option>
              <option value="en" disabled>
                English（尚不可用）
              </option>
            </select>
          </label>
        </section>
      )}

      {step === 2 && (
        <section className="flow-step" aria-labelledby="onboarding-context">
          <h1 id="onboarding-context">当前事业上下文</h1>
          <p className="lead">只保存复盘需要的最小背景，不在这里重做业务战略。</p>
          <div className="segmented-control" aria-label="业务上下文来源">
            <button
              type="button"
              className={sourceMode === "manual" ? "is-selected" : ""}
              onClick={() => setSourceMode("manual")}
            >
              手动填写
            </button>
            <label className={sourceMode === "board" ? "file-segment is-selected" : "file-segment"}>
              导入 BoardContext
              <input
                className="visually-hidden"
                type="file"
                accept="application/json,.json"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void handleBoardFile(file);
                  event.target.value = "";
                }}
              />
            </label>
          </div>
          {sourceMode === "board" && (
            <div className="source-note">
              当前内容来自只读董事会合同。CEO OS 不会原地修改董事会战略。
            </div>
          )}
          <label className="field">
            <span>当前 180 天主线</span>
            <textarea
              rows={3}
              value={context.current180DayMainline}
              placeholder="强烈建议填写；例如：验证并销售最小交付闭环"
              disabled={sourceMode === "board"}
              onChange={(event) =>
                setContext({ ...context, current180DayMainline: event.target.value })
              }
            />
          </label>
          <label className="field">
            <span>当前业务唯一瓶颈</span>
            <input
              value={context.currentConstraint}
              placeholder="可稍后补充"
              disabled={sourceMode === "board"}
              onChange={(event) =>
                setContext({ ...context, currentConstraint: event.target.value })
              }
            />
          </label>
          <details className="disclosure">
            <summary>补充今日战役、风险和禁止启动项</summary>
            <label className="field">
              <span>今日或当前业务战役</span>
              <input
                value={context.todayBusinessBattle}
                disabled={sourceMode === "board"}
                onChange={(event) =>
                  setContext({ ...context, todayBusinessBattle: event.target.value })
                }
              />
            </label>
            <label className="field">
              <span>当前最大风险</span>
              <input
                value={context.currentMaximumRisk}
                disabled={sourceMode === "board"}
                onChange={(event) =>
                  setContext({ ...context, currentMaximumRisk: event.target.value })
                }
              />
            </label>
            <label className="field">
              <span>当前禁止启动事项（每行一项）</span>
              <textarea
                rows={3}
                value={context.noStartList.join("\n")}
                disabled={sourceMode === "board"}
                onChange={(event) =>
                  setContext({
                    ...context,
                    noStartList: event.target.value
                      .split("\n")
                      .map((item) => item.trim())
                      .filter(Boolean),
                  })
                }
              />
            </label>
          </details>
        </section>
      )}

      {step === 3 && (
        <section className="flow-step" aria-labelledby="onboarding-field">
          <h1 id="onboarding-field">当前主要商业场域</h1>
          <p className="lead">每天只显示主场域的一个问题；提示不会替你生成结论。</p>
          <div className="choice-list">
            {FIELD_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={
                  profile.defaultFieldMode === option.id ? "choice-row is-selected" : "choice-row"
                }
                onClick={() => setProfile({ ...profile, defaultFieldMode: option.id })}
              >
                <span>{option.label}</span>
                <small>{option.question || "使用你设定的一条场域问题"}</small>
              </button>
            ))}
          </div>
          {profile.defaultFieldMode === "custom" && (
            <>
              <label className="field">
                <span>场域名称</span>
                <input
                  value={profile.customFieldName ?? ""}
                  onChange={(event) =>
                    setProfile({ ...profile, customFieldName: event.target.value })
                  }
                />
              </label>
              <label className="field">
                <span>每日场域问题</span>
                <input
                  value={profile.customFieldQuestion ?? ""}
                  onChange={(event) =>
                    setProfile({ ...profile, customFieldQuestion: event.target.value })
                  }
                />
              </label>
            </>
          )}
          <label className="field">
            <span>次场域（可选）</span>
            <select
              value={profile.secondaryFieldMode ?? ""}
              onChange={(event) =>
                setProfile({
                  ...profile,
                  secondaryFieldMode: (event.target.value || undefined) as FieldMode | undefined,
                })
              }
            >
              <option value="">不设置</option>
              {FIELD_OPTIONS.filter((item) => item.id !== profile.defaultFieldMode).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      {step === 4 && (
        <section className="flow-step" aria-labelledby="onboarding-constitution">
          <h1 id="onboarding-constitution">确认共同宪法</h1>
          <p className="lead">
            当前版本包含 {constitution?.principles.length ?? 0} 条原则。系统和 AI 都不能自动修改。
          </p>
          <div className="constitution-list">
            {constitution?.principles.map((principle) => (
              <details key={principle.id} className="constitution-item">
                <summary>
                  <span>{String(principle.order).padStart(2, "0")}</span>
                  {principle.text}
                </summary>
                <div>
                  <p>
                    <strong>正例：</strong>
                    {principle.positiveExamples[0]}
                  </p>
                  <p>
                    <strong>反例：</strong>
                    {principle.negativeExamples[0]}
                  </p>
                  <p>
                    <strong>复核触发：</strong>
                    {principle.reviewTriggers[0]}
                  </p>
                </div>
              </details>
            ))}
          </div>
          <label className="check-row">
            <input
              type="checkbox"
              checked={constitutionChecked}
              onChange={(event) => setConstitutionChecked(event.target.checked)}
            />
            <span>我确认使用当前共同宪法 v{constitution?.version ?? "1.0.0"}。</span>
          </label>
        </section>
      )}

      {step === 5 && (
        <section className="flow-step" aria-labelledby="onboarding-privacy">
          <h1 id="onboarding-privacy">本地存储与隐私</h1>
          <div className="privacy-rail">
            <p>数据默认保存在当前浏览器 IndexedDB。</p>
            <p>本地优先不等于绝对加密；扩展、设备控制权和恶意软件不在应用防御范围内。</p>
            <p>清理浏览器数据可能导致丢失；已经导出的文件需要你自行保管。</p>
            <p>私人原文默认不会进入董事会摘要。</p>
          </div>
          <label className="check-row">
            <input
              type="checkbox"
              checked={privacyChecked}
              onChange={(event) => setPrivacyChecked(event.target.checked)}
            />
            <span>我理解本地存储边界和备份责任。</span>
          </label>
          <button
            type="button"
            className="secondary-button full-width"
            onClick={() => {
              void createFullBackup(database).then((backup) =>
                downloadJson(
                  backup,
                  `ceo-os-private-backup-${backup.produced_at.slice(0, 10)}.json`,
                ),
              );
            }}
          >
            可选：创建首次私人备份
          </button>
        </section>
      )}

      <footer className="flow-actions">
        {step > 1 ? (
          <button type="button" className="secondary-button" onClick={() => void move(-1)}>
            上一步
          </button>
        ) : (
          <span />
        )}
        {step < 5 ? (
          <button
            type="button"
            className="primary-button"
            disabled={!canContinue}
            onClick={() => void move(1)}
          >
            继续
          </button>
        ) : (
          <button
            type="button"
            className="primary-button"
            disabled={!canContinue}
            onClick={() => void finish()}
          >
            完成 Onboarding
          </button>
        )}
      </footer>
    </main>
  );
}
